import React, { useState, useEffect, useCallback } from "react";
import "./Hero.css";
import { Link, useNavigate } from "react-router-dom";





/* ── Slide data ── */
const SLIDES = [
  {
    eyebrow: "Infrastructure & Reliability",
    prefix: "Modern",
    bracket: "Cloud & DevOps",
    suffix: "Solutions",
    sub: "Reliable, auto-scaling infrastructure that deploys in minutes and stays up 99.99% of the time.",
    accent: "orange",
    tag: "01",
    metrics: [{ val: "99.99%", lbl: "Uptime SLA" }, { val: "<2 min", lbl: "Deploy time" }, { val: "∞", lbl: "Scalability" }],
  },
  {
    eyebrow: "Intelligence & Automation",
    prefix: "Advanced",
    bracket: "Software & AI",
    suffix: "Engineering",
    sub: "Custom machine-learning pipelines and intelligent automation that turn your data into a competitive edge.",
    accent: "green",
    tag: "02",
    metrics: [{ val: "10×", lbl: "Faster Delivery" }, { val: "AI-First", lbl: "Architecture" }, { val: "Real-Time", lbl: "Intelligence" }],
  },
  {
    eyebrow: "Web & Digital Presence",
    prefix: "Full-Stack",
    bracket: "Web Development",
    suffix: "Services",
    sub: "Fast, secure, and SEO-ready web applications — built to perform on every device and at every scale.",
    accent: "orange",
    tag: "03",
    metrics: [{ val: "100", lbl: "PageSpeed Score" }, { val: "Mobile-First", lbl: "Design" }, { val: "SEO-Ready", lbl: "From Day 1" }],
  },
];

const DURATION = 5500;

/* ── SVG node positions ── */
// const NODES = [
//   { cx: 72,  cy: 68  },{ cx: 220, cy: 40  },{ cx: 340, cy: 110 },
//   { cx: 180, cy: 190 },{ cx: 60,  cy: 240 },{ cx: 290, cy: 280 },
//   { cx: 140, cy: 340 },{ cx: 380, cy: 200 },{ cx: 240, cy: 380 },
//   { cx: 420, cy: 360 },{ cx: 50,  cy: 420 },{ cx: 310, cy: 440 },
// ];
// const EDGES = [
//   [0,1],[1,2],[2,3],[3,0],[2,7],[3,5],[4,5],[4,6],
//   [5,6],[6,8],[7,9],[8,9],[8,11],[9,10],[10,11],[3,7],[0,4],
// ];

const NODES = [
  // Top Layer
  { id: 0, cx: 72, cy: 68 },
  { id: 1, cx: 220, cy: 40 },
  { id: 2, cx: 340, cy: 110 },

  // Middle Layer
  { id: 3, cx: 180, cy: 190 },
  { id: 4, cx: 60, cy: 240 },
  { id: 5, cx: 290, cy: 280 },
  { id: 7, cx: 380, cy: 200 }, // Placed here to match vertical hierarchy

  // Bottom Layer
  { id: 6, cx: 140, cy: 340 },
  { id: 8, cx: 240, cy: 380 },
  { id: 9, cx: 420, cy: 360 },
  { id: 10, cx: 50, cy: 420 },
  { id: 11, cx: 310, cy: 440 },
];


const EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 0], [2, 7], [3, 5], [4, 5], [4, 6],
  [5, 6], [6, 8], [7, 9], [8, 9], [8, 11], [9, 10], [10, 11], [3, 7], [0, 4],
];

function NodeMesh({ isOrange }) {
  const color = isOrange ? "#E8571A" : "#1d57d3";
  return (
    <svg className="node-mesh" viewBox="0 0 460 480" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="meshGrad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={color} stopOpacity=".14" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </radialGradient>
        <filter id="meshGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <ellipse cx="230" cy="240" rx="210" ry="220" fill="url(#meshGrad)" />
      {EDGES.map(([a, b], i) => (
        <line key={i}
          x1={NODES[a].cx} y1={NODES[a].cy}
          x2={NODES[b].cx} y2={NODES[b].cy}
          stroke={color} strokeWidth=".9" strokeOpacity=".2"
          className={`mesh-edge me${i % 5}`}
        />
      ))}
      {NODES.map((n, i) => (
        <g key={i} filter="url(#meshGlow)" className={`mesh-node mn${i % 4}`}>
          <circle cx={n.cx} cy={n.cy} r="10" fill={color} fillOpacity=".08" />
          <circle cx={n.cx} cy={n.cy} r="4" fill={color} fillOpacity=".65" />
        </g>
      ))}
      {/* Travelling packet */}
      <circle r="3.5" fill="#ffffff" fillOpacity=".9">
        <animateMotion dur="5s" repeatCount="indefinite">
          <mpath href="#ppath" />
        </animateMotion>
      </circle>
      <path id="ppath"
        d={`M${NODES[0].cx},${NODES[0].cy} L${NODES[1].cx},${NODES[1].cy} L${NODES[2].cx},${NODES[2].cy} L${NODES[7].cx},${NODES[7].cy} L${NODES[9].cx},${NODES[9].cy} L${NODES[8].cx},${NODES[8].cy} L${NODES[6].cx},${NODES[6].cy} L${NODES[4].cx},${NODES[4].cy} Z`}
        fill="none"
      />
    </svg>
  );
}

export default function Hero({ scrollTo }) {

  const navigate = useNavigate();

  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("in");   /* "in" | "out" */
  const [progress, setProgress] = useState(0);

  const goTo = useCallback((next) => {
    setPhase("out");
    setTimeout(() => {
      setIdx(next);
      setProgress(0);
      setPhase("in");
    }, 450);
  }, []);

  useEffect(() => {
    let raf;
    const t0 = Date.now();
    const tick = () => {
      const pct = Math.min(((Date.now() - t0) / DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) goTo((idx + 1) % SLIDES.length);
      else raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [idx, goTo]);

  const s = SLIDES[idx];
  const isOrange = s.accent === "orange";

  const accentVar = isOrange ? "var(--orange)" : "#185df3"; /* var(--blue) */
  const accentHex = isOrange ? "#E8571A" : "#1d57d3";
  const circumference = 2 * Math.PI * 15; // r=15

  return (
    <section className="hero" id="home">
      <div className="hero-grid" />

      {/* ── Main content area ── */}
      <div className={`hero-body hero-${phase}`}>
        {/* LEFT */}
        <div className="hero-left">
          <div className="hero-meta">
            <span className="slide-counter">{s.tag} <span className="sc-sep">/</span> 0{SLIDES.length}</span>
            <span className="hero-eyebrow">{s.eyebrow}</span>
          </div>

          <h1 className="hero-headline">
            <span className="h-plain">{s.prefix}&nbsp;</span>
            <span className="h-bracket" style={{ "--acc": accentVar }}>
              <span className="br-l">[</span>{s.bracket}<span className="br-r">]</span>
            </span>
            <br />
            <span className="h-plain">{s.suffix}</span>
          </h1>

          <p className="hero-sub">{s.sub}</p>

          <div className="metrics-row">
            {s.metrics.map((m, i) => (
              <div key={i} className="metric-block">
                <span className="metric-val" style={{ color: accentVar }}>{m.val}</span>
                <span className="metric-lbl">{m.lbl}</span>
              </div>
            ))}
          </div>

          <div className="hero-actions">



            <button
              className="btn-primary-hero"
              style={{ "--acc": accentVar, "--acc-hex": accentHex }}
            >
              <Link
                to="/contact"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "block",
                  width: "100%",
                  height: "100%",
                }}
              >
                Start a Project <span>→</span>
              </Link>
            </button>

           <button
  className="btn-ghost-hero"
  onClick={() => navigate("/portfolio")}
>
  View Our Work
</button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="hero-right">
          <NodeMesh isOrange={isOrange} />

          {/* Floating stat chips */}
          <div className="stat-chip chip-a" style={{ borderColor: accentHex + "40" }}>
            <span className="chip-dot" style={{ background: accentVar }} />
            <span className="chip-val" style={{ color: accentVar }}>{s.metrics[0].val}</span>
            <span className="chip-lbl">{s.metrics[0].lbl}</span>
          </div>
          <div className="stat-chip chip-b" style={{ borderColor: accentHex + "40" }}>
            <span className="chip-dot" style={{ background: accentVar }} />
            <span className="chip-val" style={{ color: accentVar }}>{s.metrics[1].val}</span>
            <span className="chip-lbl">{s.metrics[1].lbl}</span>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="hero-bar">
        {/* Slide nav */}
        <div className="slide-nav">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              className={`dot-btn${i === idx ? " active" : ""}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            >
              {i === idx ? (
                <svg viewBox="0 0 36 36" className="ring-svg">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="rgba(255,255,255,.15)" strokeWidth="2" />
                  <circle cx="18" cy="18" r="15" fill="none"
                    stroke={accentHex} strokeWidth="2.5"
                    strokeDasharray={`${(progress / 100) * circumference} ${circumference}`}
                    strokeLinecap="round" transform="rotate(-90 18 18)"
                  />
                  <circle cx="18" cy="18" r="4" fill={accentHex} />
                </svg>
              ) : (
                <span className="dot-inner" />
              )}
            </button>
          ))}
        </div>

        {/* Slide title labels */}
        <div className="slide-titles">
          {SLIDES.map((sl, i) => (
            <button key={i}
              className={`slide-title-btn${i === idx ? " active" : ""}`}
              onClick={() => goTo(i)}
              style={i === idx ? { color: accentVar } : {}}
            >
              <span className="stb-tag">{sl.tag}</span>
              {sl.bracket}
            </button>
          ))}
        </div>

        {/* Scroll cue */}
        <button className="scroll-cue" onClick={() => scrollTo && scrollTo("services")}>
          <span className="scroll-rail"><span className="scroll-bead" /></span>
          <span className="scroll-text">Scroll</span>
        </button>
      </div>
    </section>
  );
}
